package com.schoolconnect.app.feature.schools

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.schoolconnect.app.designsystem.AppDialogActionStyle
import com.schoolconnect.app.designsystem.AppDialogBody
import com.schoolconnect.app.designsystem.AppDialogColors
import com.schoolconnect.app.designsystem.AppDialogDivider
import com.schoolconnect.app.designsystem.AppDialogDateField
import com.schoolconnect.app.designsystem.AppDialogFieldColumn
import com.schoolconnect.app.designsystem.AppDialogFooter
import com.schoolconnect.app.designsystem.AppDialogFooterAction
import com.schoolconnect.app.designsystem.AppDialogHeader
import com.schoolconnect.app.designsystem.AppDialogHelperSize
import com.schoolconnect.app.designsystem.AppDialogScaffold
import com.schoolconnect.app.designsystem.AppDialogSectionLabel
import com.schoolconnect.app.designsystem.AppDialogSelectField
import com.schoolconnect.app.designsystem.AppDialogSize
import com.schoolconnect.app.designsystem.AppDialogTextField
import com.schoolconnect.app.repository.systemlookup.SystemLookupOptions
import com.schoolconnect.app.repository.systemlookup.SystemLookupRepository
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.users
import org.jetbrains.compose.resources.painterResource

private val FilterModalMuted = com.schoolconnect.app.designsystem.AppMuted
private val FilterModalDialogColors = com.schoolconnect.app.designsystem.AppDialogDefaultColors


@Composable
internal fun SchoolsFilterModal(
    draft: SchoolFilterCriteria,
    onDraftChange: (SchoolFilterCriteria) -> Unit,
    onApply: () -> Unit,
    onReset: () -> Unit,
    onCancel: () -> Unit,
    modifier: Modifier = Modifier
) {
    var errorMessage by remember { mutableStateOf("") }
    val lookupSnapshot by SystemLookupRepository.snapshot.collectAsState()
    val schoolBoardOptions = remember(lookupSnapshot) { SystemLookupOptions.labels("SCHOOL_BOARD") }
    val schoolTypeOptions = remember(lookupSnapshot) { SystemLookupOptions.labels("SCHOOL_TYPE") }
    val schoolMediumOptions = remember(lookupSnapshot) { SystemLookupOptions.labels("SCHOOL_MEDIUM") }

    AppDialogScaffold(
        onDismiss = onCancel,
        size = AppDialogSize.Standard,
        maxDialogHeight = 720.dp,
        shape = RoundedCornerShape(10.dp),
        colors = FilterModalDialogColors
    ) {
        AppDialogHeader(
            title = "Filter Schools",
            subtitle = "Apply filters to narrow down school list",
            onClose = onCancel,
            closeContentDescription = "Close filter modal",
            colors = FilterModalDialogColors
        )
        AppDialogDivider(colors = FilterModalDialogColors)

        AppDialogBody(
            compactBreakpoint = 560.dp,
            showVerticalScrollbar = true,
            colors = FilterModalDialogColors,
            horizontalPadding = 24.dp,
            compactHorizontalPadding = 24.dp,
            verticalPadding = 24.dp
        ) { compact ->
            Column(verticalArrangement = Arrangement.spacedBy(24.dp)) {
                FilterTripleSection(
                    isCompact = compact,
                    first = {
                        FilterSelectField(
                            label = "Board",
                            value = draft.board,
                            placeholder = "Select board",
                            options = schoolBoardOptions,
                            onValueChange = { onDraftChange(draft.copy(board = it)) }
                        )
                    },
                    second = {
                        FilterSelectField(
                            label = "School Type",
                            value = draft.schoolType,
                            placeholder = "Select type",
                            options = schoolTypeOptions,
                            onValueChange = { onDraftChange(draft.copy(schoolType = it)) }
                        )
                    },
                    third = {
                        FilterSelectField(
                            label = "Medium",
                            value = draft.medium,
                            placeholder = "Select medium",
                            options = schoolMediumOptions,
                            onValueChange = { onDraftChange(draft.copy(medium = it)) }
                        )
                    }
                )

                FilterRangeSection(
                    title = "Student Count Range",
                    firstLabel = "Min Students",
                    firstValue = draft.minStudents,
                    firstPlaceholder = "Min",
                    secondLabel = "Max Students",
                    secondValue = draft.maxStudents,
                    secondPlaceholder = "Max",
                    leadingIcon = Res.drawable.users,
                    onFirstChange = { onDraftChange(draft.copy(minStudents = it)) },
                    onSecondChange = { onDraftChange(draft.copy(maxStudents = it)) }
                )

                FilterDateRangeSection(
                    fromLabel = "Created from",
                    toLabel = "Created to",
                    from = draft.createdFrom,
                    to = draft.createdTo,
                    onFromChange = { onDraftChange(draft.copy(createdFrom = it)) },
                    onToChange = { onDraftChange(draft.copy(createdTo = it)) }
                )

                FilterDateRangeSection(
                    fromLabel = "Updated from",
                    toLabel = "Updated to",
                    from = draft.updatedFrom,
                    to = draft.updatedTo,
                    onFromChange = { onDraftChange(draft.copy(updatedFrom = it)) },
                    onToChange = { onDraftChange(draft.copy(updatedTo = it)) }
                )

                Box(modifier = Modifier.fillMaxWidth(if (compact) 1f else 0.333f)) {
                    FilterSelectField(
                        label = "Status",
                        value = draft.status.displayLabel(),
                        placeholder = "All",
                        options = schoolStatusFilterLabels,
                        onValueChange = { label ->
                            onDraftChange(draft.copy(status = label.toSchoolStatusFilter()))
                        }
                    )
                }
            }
        }

        if (errorMessage.isNotBlank()) {
            Text(
                text = errorMessage,
                color = com.schoolconnect.app.designsystem.AppDanger,
                fontSize = AppDialogHelperSize,
                fontWeight = FontWeight.Medium,
                modifier = Modifier
                    .fillMaxWidth()
                    .background(Color(0xFFFEF2F2))
                    .padding(horizontal = 24.dp, vertical = 12.dp)
            )
        }
        AppDialogFooter(
            actions = listOf(
                AppDialogFooterAction(label = "Cancel", onClick = onCancel),
                AppDialogFooterAction(
                    label = "Reset",
                    onClick = {
                        errorMessage = ""
                        onReset()
                    }
                ),
                AppDialogFooterAction(
                    label = "Apply",
                    onClick = {
                        errorMessage = validateSchoolFilter(draft)
                        if (errorMessage.isBlank()) onApply()
                    },
                    style = AppDialogActionStyle.Primary
                )
            ),
            colors = FilterModalDialogColors,
            contentPadding = PaddingValues(horizontal = 24.dp)
        )
    }
}

@Composable
private fun FilterRangeSection(
    title: String,
    firstLabel: String,
    firstValue: String,
    firstPlaceholder: String,
    secondLabel: String,
    secondValue: String,
    secondPlaceholder: String,
    leadingIcon: org.jetbrains.compose.resources.DrawableResource,
    onFirstChange: (String) -> Unit,
    onSecondChange: (String) -> Unit
) {
    Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
        AppDialogSectionLabel(text = title, colors = FilterModalDialogColors, fontSize = 16.sp)
        FilterRangeFields(
            firstLabel = firstLabel,
            firstValue = firstValue,
            firstPlaceholder = firstPlaceholder,
            secondLabel = secondLabel,
            secondValue = secondValue,
            secondPlaceholder = secondPlaceholder,
            leadingIcon = leadingIcon,
            onFirstChange = onFirstChange,
            onSecondChange = onSecondChange
        )
    }
}

@Composable
private fun FilterRangeFields(
    firstLabel: String,
    firstValue: String,
    firstPlaceholder: String,
    secondLabel: String,
    secondValue: String,
    secondPlaceholder: String,
    leadingIcon: org.jetbrains.compose.resources.DrawableResource,
    onFirstChange: (String) -> Unit,
    onSecondChange: (String) -> Unit
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(16.dp),
        verticalAlignment = Alignment.Bottom
    ) {
        Box(modifier = Modifier.weight(1f)) {
            FilterFieldSection(firstLabel) {
                FilterTextField(
                    value = firstValue,
                    placeholder = firstPlaceholder,
                    leadingIcon = { FilterLeadingIcon(leadingIcon) },
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                    onValueChange = onFirstChange
                )
            }
        }
        Text(text = "–", color = FilterModalMuted, fontSize = 14.sp, modifier = Modifier.padding(bottom = 12.dp))
        Box(modifier = Modifier.weight(1f)) {
            FilterFieldSection(secondLabel) {
                FilterTextField(
                    value = secondValue,
                    placeholder = secondPlaceholder,
                    leadingIcon = { FilterLeadingIcon(leadingIcon) },
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                    onValueChange = onSecondChange
                )
            }
        }
    }
}

@Composable
private fun FilterDateRangeSection(
    fromLabel: String,
    toLabel: String,
    from: String,
    to: String,
    onFromChange: (String) -> Unit,
    onToChange: (String) -> Unit
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        Box(modifier = Modifier.weight(1f)) {
            FilterFieldSection(fromLabel) {
                AppDialogDateField(value = from, onValueChange = onFromChange, colors = FilterModalDialogColors)
            }
        }
        Box(modifier = Modifier.weight(1f)) {
            FilterFieldSection(toLabel) {
                AppDialogDateField(value = to, onValueChange = onToChange, colors = FilterModalDialogColors)
            }
        }
    }
}

@Composable
private fun FilterLeadingIcon(resource: org.jetbrains.compose.resources.DrawableResource) {
    Icon(
        painter = painterResource(resource),
        contentDescription = null,
        tint = FilterModalMuted,
        modifier = Modifier.size(16.dp)
    )
}

@Composable
private fun FilterTripleSection(
    isCompact: Boolean,
    first: @Composable () -> Unit,
    second: @Composable () -> Unit,
    third: @Composable () -> Unit
) {
    if (isCompact) {
        Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
            first()
            second()
            third()
        }
    } else {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            Box(modifier = Modifier.weight(1f)) { first() }
            Box(modifier = Modifier.weight(1f)) { second() }
            Box(modifier = Modifier.weight(1f)) { third() }
        }
    }
}

@Composable
private fun FilterFieldSection(
    label: String,
    field: @Composable () -> Unit
) {
    AppDialogFieldColumn(
        label = label,
        colors = FilterModalDialogColors,
        contentSpacing = 6.dp,
        field = field
    )
}

@Composable
private fun FilterSelectField(
    label: String,
    value: String,
    placeholder: String,
    options: List<String>,
    onValueChange: (String) -> Unit
) {
    var expanded by remember { mutableStateOf(false) }
    FilterFieldSection(label = label) {
        AppDialogSelectField(
            value = value,
            placeholder = placeholder,
            expanded = expanded,
            options = options,
            onToggleExpanded = { expanded = !expanded },
            onDismissExpanded = { expanded = false },
            onSelected = {
                onValueChange(it)
                expanded = false
            },
            colors = FilterModalDialogColors
        )
    }
}

@Composable
private fun FilterTextField(
    value: String,
    placeholder: String,
    leadingIcon: @Composable () -> Unit,
    onValueChange: (String) -> Unit,
    keyboardOptions: KeyboardOptions = KeyboardOptions.Default
) {
    AppDialogTextField(
        value = value,
        onValueChange = onValueChange,
        placeholder = placeholder,
        leadingIcon = leadingIcon,
        colors = FilterModalDialogColors,
        keyboardType = keyboardOptions.keyboardType
    )
}

private fun validateSchoolFilter(filter: SchoolFilterCriteria): String {
    return ""
}

private val schoolStatusFilterLabels = listOf("All", "Active", "Inactive")
